import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChartLine,
  faCircleCheck,
  faClock,
  faListCheck,
  faTicket,
  faUsers,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";

const cards = [
  { key: "totalOrganizations", label: "Organizations", helper: "Visible in current scope", icon: faBuilding, tone: "bg-sky-500" },
  { key: "totalCustomers", label: "Customers", helper: "Accounts in report", icon: faUsers, tone: "bg-emerald-500" },
  { key: "totalCustomerUsers", label: "Customer Users", helper: "Tracked active contacts", icon: faUserTie, tone: "bg-amber-500" },
  { key: "totalEmployees", label: "Assigned Employees", helper: "Employees on tickets", icon: faUsers, tone: "bg-rose-500" },
  { key: "totalTickets", label: "Tickets", helper: "Customer issues created", icon: faTicket, tone: "bg-slate-900" },
  { key: "totalTasks", label: "Generated Tasks", helper: "Tasks from customer tickets", icon: faListCheck, tone: "bg-cyan-600" },
  { key: "resolvedTickets", label: "Resolved Tickets", helper: "Closed in selected range", icon: faCircleCheck, tone: "bg-lime-600" },
  { key: "openTickets", label: "Open Tickets", helper: "New, in progress, on hold", icon: faClock, tone: "bg-orange-500" },
];

const formatCompact = (value) =>
  new Intl.NumberFormat(undefined, {
    notation: value >= 1000 ? "compact" : "standard",
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(Number(value || 0));

const formatMetric = (key, summary) => {
  if (key === "totalOrganizations" || key === "totalCustomers" || key === "totalCustomerUsers" || key === "totalEmployees" || key === "totalTickets" || key === "totalTasks" || key === "resolvedTickets" || key === "openTickets") {
    return formatCompact(summary?.[key] || 0);
  }

  return String(summary?.[key] ?? 0);
};

export default function KpiSummaryCards({ summary }) {
  const rate = Number(summary?.resolutionRate || 0);
  const hours = Number(summary?.averageResolutionHours || 0);
  const averageTasks = Number(summary?.averageTasksPerTicket || 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card.key} className="overflow-hidden rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{card.label}</p>
                <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{formatMetric(card.key, summary)}</p>
                <p className="mt-2 text-sm text-slate-500">{card.helper}</p>
              </div>
              <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white ${card.tone}`}>
                <FontAwesomeIcon icon={card.icon} />
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <FontAwesomeIcon icon={faChartLine} />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Resolution Rate</p>
              <p className="mt-2 text-2xl font-semibold text-slate-950">{rate.toFixed(1)}%</p>
              <p className="mt-1 text-sm text-slate-500">Customer issue resolution analytics across the selected window.</p>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <FontAwesomeIcon icon={faClock} />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Average Resolution</p>
              <p className="mt-2 text-2xl font-semibold text-slate-950">{hours.toFixed(1)} hrs</p>
              <p className="mt-1 text-sm text-slate-500">Useful for SLA-style admin reporting and customer expectation tracking.</p>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
              <FontAwesomeIcon icon={faListCheck} />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Tasks Per Ticket</p>
              <p className="mt-2 text-2xl font-semibold text-slate-950">{averageTasks.toFixed(2)}</p>
              <p className="mt-1 text-sm text-slate-500">Measures how much operational work each customer issue generates.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
